<template>
  <div class="mt-4 mx-3 row white-box px-3 py-4 collection-settings">
    <div class="white-box-top"></div>
    <div class="col-md-4 col-lg-2">
      <nav class="left-nav">
        <div
          class="items"
          v-scroll-spy-link="{ selector: 'a', class: 'btn-primary' }"
          v-scroll-spy-active="{ selector: 'a div', class: 'btn-primary' }"
        >
          <div class="items">
            <a href="#general">
              <div class="btn w-100 btn-simple text-start">General</div>
            </a>
          </div>
        </div>
      </nav>
    </div>
    <div class="col-md-8 col-lg-10" v-scroll-spy="{ offset: 120 }">
      <section id="general">
        <div class="settings-heading">
          <div>
            <h2>Collection settings</h2>
            <p class="text-muted mb-0">
              Decide which signals influence the order in which warehouse work
              is picked.
            </p>
          </div>
          <span class="status-pill" :class="{ dirty: isDirty }">
            {{ isDirty ? "Unsaved changes" : "All changes saved" }}
          </span>
        </div>

        <div class="info-card mt-4">
          <strong>How priority works</strong>
          <p class="mb-0 mt-1">
            The enabled weights are combined into one score. A higher weight
            makes that signal more important, but does not disable the others.
            New collection runs use the saved values.
          </p>
        </div>

        <div
          v-for="priority in priorities"
          :key="priority.key"
          class="priority-card mt-3"
          :class="{ disabled: !priority.enabled }"
        >
          <div class="priority-card-header">
            <div>
              <h3>{{ priority.title }}</h3>
              <p>{{ priority.description }}</p>
            </div>
            <label class="switch" :aria-label="`Enable ${priority.title}`">
              <input
                type="checkbox"
                :checked="priority.enabled"
                @change="togglePriority(priority.key, $event.target.checked)"
              />
              <span class="switch-slider"></span>
            </label>
          </div>

          <div class="priority-controls" :class="{ muted: !priority.enabled }">
            <input
              type="range"
              class="form-range"
              :value="priority.value"
              @input="updatePriority(priority.key, $event.target.value)"
              min="0"
              max="10"
              step="0.01"
              :disabled="!priority.enabled"
              :aria-label="`${priority.title} weight`"
            />
            <div class="value-row">
              <span class="range-label">Low impact <span>0</span></span>
              <div class="number-control">
                <input
                  type="number"
                  class="form-control"
                  :value="priority.value"
                  @input="updatePriority(priority.key, $event.target.value)"
                  min="0"
                  max="999"
                  step="0.01"
                  :disabled="!priority.enabled"
                />
                <span>weight</span>
              </div>
              <span class="range-label text-end"
                >High impact <span>10+</span></span
              >
            </div>
          </div>

          <div class="priority-footer">
            <span>{{ priority.hint }}</span>
            <button
              type="button"
              class="link-button"
              @click="setPriority(priority, priority.defaultValue)"
            >
              Use default ({{ priority.defaultValue }})
            </button>
          </div>
        </div>

        <div class="preview-card mt-4">
          <div class="preview-heading">
            <div>
              <h3>Priority preview</h3>
              <p>Relative influence of the enabled signals.</p>
            </div>
            <span>{{ enabledWeightTotal.toFixed(2) }} total weight</span>
          </div>
          <div
            v-for="priority in priorities"
            :key="`bar-${priority.key}`"
            class="preview-row"
          >
            <span>{{ priority.shortTitle }}</span>
            <div class="preview-track">
              <div
                class="preview-bar"
                :style="{ width: previewWidth(priority) + '%' }"
              ></div>
            </div>
            <strong>{{
              priority.enabled ? formatValue(priority.value) : "Off"
            }}</strong>
          </div>
        </div>

        <div class="actions mt-4">
          <button
            type="button"
            class="btn btn-light"
            @click="resetDefaults"
            :disabled="saving"
          >
            Reset defaults
          </button>
          <button
            type="button"
            class="btn btn-primary"
            @click="save"
            :disabled="saving || !isDirty"
          >
            {{ saving ? "Saving..." : "Save settings" }}
          </button>
        </div>
        <p
          v-if="saveMessage"
          class="save-message"
          :class="{ error: saveError }"
        >
          {{ saveMessage }}
        </p>
      </section>
    </div>
  </div>
</template>

<script>
import http from "../../modules/http";

export default {
  name: "CollectionSettings",
  data() {
    return {
      collectionDatePoint: 1,
      collectionProductPoint: 0.01,
      collectionDeliveryPoint: 0.1,
      saving: false,
      saveMessage: "",
      saveError: false,
      savedSnapshot: "",
    };
  },
  computed: {
    priorities() {
      return [
        {
          key: "date",
          title: "Order age",
          shortTitle: "Order age",
          description: "Prioritize orders that have been waiting longer.",
          hint: "Best for reducing backlog and preventing old orders from being missed.",
          defaultValue: 1,
          enabled: this.collectionDatePoint > 0,
          value: this.collectionDatePoint,
        },
        {
          key: "product",
          title: "Product similarity",
          shortTitle: "Product similarity",
          description:
            "Group orders with similar products or locations together.",
          hint: "Can reduce walking when many orders contain related products.",
          defaultValue: 0.01,
          enabled: this.collectionProductPoint > 0,
          value: this.collectionProductPoint,
        },
        {
          key: "delivery",
          title: "Courier",
          shortTitle: "Delivery method",
          description:
            "Prioritize orders according to the delivery method's courier score.",
          hint: "Useful when some carriers, routes, or pickup points should be grouped.",
          defaultValue: 0.1,
          enabled: this.collectionDeliveryPoint > 0,
          value: this.collectionDeliveryPoint,
        },
      ];
    },
    enabledWeightTotal() {
      return this.priorities.reduce(
        (total, priority) =>
          total + (priority.enabled ? Number(priority.value) || 0 : 0),
        0
      );
    },
    isDirty() {
      return this.settingsSnapshot() !== this.savedSnapshot;
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {
      http.fetch("/collection-settings").then((data) => {
        this.collectionDatePoint = Number(data.collection_date_point) || 0;
        this.collectionProductPoint =
          Number(data.collection_product_point) || 0;
        this.collectionDeliveryPoint =
          Number(data.collection_delivery_point) || 0;
        this.savedSnapshot = this.settingsSnapshot();
      });
    },
    settingsSnapshot() {
      return JSON.stringify([
        Number(this.collectionDatePoint) || 0,
        Number(this.collectionProductPoint) || 0,
        Number(this.collectionDeliveryPoint) || 0,
      ]);
    },
    setPriority(priority, value) {
      this.updatePriority(priority.key, value);
    },
    updatePriority(key, value) {
      const property = `collection${
        key.charAt(0).toUpperCase() + key.slice(1)
      }Point`;
      this[property] = Math.max(0, Math.min(999, Number(value) || 0));
    },
    togglePriority(key, enabled) {
      if (!enabled) {
        this.updatePriority(key, 0);
      } else {
        const priority = this.priorities.find((item) => item.key === key);
        this.updatePriority(key, priority.defaultValue);
      }
    },
    previewWidth(priority) {
      if (!priority.enabled || !this.enabledWeightTotal) return 0;
      return Math.max(
        4,
        Math.round((Number(priority.value) / this.enabledWeightTotal) * 100)
      );
    },
    formatValue(value) {
      return Number(value || 0)
        .toFixed(2)
        .replace(/\.00$/, "");
    },
    resetDefaults() {
      this.collectionDatePoint = 1;
      this.collectionProductPoint = 0.01;
      this.collectionDeliveryPoint = 0.1;
      this.saveMessage = "";
    },
    save() {
      this.saving = true;
      this.saveMessage = "";
      this.saveError = false;
      http
        .fetch(
          "/collection-settings",
          {
            collection_date_point: this.collectionDatePoint,
            collection_product_point: this.collectionProductPoint,
            collection_delivery_point: this.collectionDeliveryPoint,
          },
          true,
          "POST"
        )
        .then(() => {
          this.saving = false;
          this.savedSnapshot = this.settingsSnapshot();
          this.saveMessage = "Collection priorities saved.";
        })
        .catch(() => {
          this.saving = false;
          this.saveError = true;
          this.saveMessage = "Could not save the collection priorities.";
        });
    },
  },
};
</script>

<style scoped>
.collection-settings {
  color: #263238;
}

.settings-heading,
.priority-card-header,
.preview-heading,
.actions,
.value-row,
.priority-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.settings-heading h2,
.priority-card h3,
.preview-card h3 {
  margin: 0;
}

.settings-heading h2 {
  font-size: 1.55rem;
}

.priority-card h3,
.preview-card h3 {
  font-size: 1rem;
  font-weight: 700;
}

.status-pill {
  padding: 6px 10px;
  border-radius: 999px;
  color: #27734d;
  background: #e8f6ee;
  font-size: 0.78rem;
  font-weight: 600;
  white-space: nowrap;
}

.status-pill.dirty {
  color: #8a5b00;
  background: #fff4d6;
}

.info-card,
.preview-card {
  padding: 16px 18px;
  border: 1px solid #e5e9ed;
  border-radius: 10px;
  background: #f8fafb;
}

.info-card p,
.preview-heading p,
.priority-card p {
  color: #71808a;
  font-size: 0.88rem;
}

.priority-card {
  padding: 18px;
  border: 1px solid #e5e9ed;
  border-radius: 10px;
  background: #fff;
  transition: opacity 0.15s ease, border-color 0.15s ease;
}

.priority-card.disabled {
  opacity: 0.68;
}

.priority-card-header p {
  margin: 5px 0 0;
}

.priority-controls {
  margin-top: 14px;
}

.priority-controls.muted .form-range {
  cursor: not-allowed;
}

.form-range {
  accent-color: #3b82f6;
}

.range-label {
  color: #8a969e;
  font-size: 0.76rem;
}

.range-label span {
  display: block;
  color: #4a5962;
  font-weight: 600;
}

.number-control {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 150px;
}

.number-control input {
  text-align: right;
}

.number-control span {
  color: #8a969e;
  font-size: 0.78rem;
}

.priority-footer {
  align-items: flex-start;
  margin-top: 12px;
  color: #71808a;
  font-size: 0.8rem;
}

.link-button {
  padding: 0;
  border: 0;
  color: #2878d4;
  background: transparent;
  font-size: 0.8rem;
  white-space: nowrap;
}

.link-button:hover {
  text-decoration: underline;
}

.switch {
  position: relative;
  display: inline-block;
  flex: 0 0 auto;
  width: 42px;
  height: 24px;
  margin: 0;
}

.switch input {
  width: 0;
  height: 0;
  opacity: 0;
}

.switch-slider {
  position: absolute;
  inset: 0;
  border-radius: 24px;
  background: #cbd3d8;
  cursor: pointer;
  transition: 0.2s;
}

.switch-slider::before {
  position: absolute;
  bottom: 3px;
  left: 3px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  content: "";
  transition: 0.2s;
}

.switch input:checked + .switch-slider {
  background: #3b82f6;
}

.switch input:checked + .switch-slider::before {
  transform: translateX(18px);
}

.preview-heading {
  align-items: flex-start;
}

.preview-heading > span {
  color: #71808a;
  font-size: 0.78rem;
}

.preview-row {
  display: grid;
  grid-template-columns: 125px 1fr 42px;
  align-items: center;
  gap: 12px;
  margin-top: 14px;
  font-size: 0.82rem;
}

.preview-track {
  height: 8px;
  overflow: hidden;
  border-radius: 8px;
  background: #e8edf0;
}

.preview-bar {
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #74aaf0, #2878d4);
  transition: width 0.2s ease;
}

.preview-row strong {
  text-align: right;
  color: #4a5962;
}

.actions {
  justify-content: flex-end;
}

.save-message {
  margin: 10px 0 0;
  color: #27734d;
  font-size: 0.85rem;
  text-align: right;
}

.save-message.error {
  color: #b42318;
}

@media (max-width: 640px) {
  .settings-heading,
  .priority-card-header,
  .priority-footer {
    align-items: flex-start;
  }

  .settings-heading,
  .priority-footer {
    flex-direction: column;
  }

  .priority-footer .link-button {
    align-self: flex-start;
  }

  .preview-row {
    grid-template-columns: 105px 1fr 34px;
    gap: 8px;
  }
}
</style>
